import { useCallback, useEffect, useRef, useState } from 'react';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useUpdateCoverDesignMutation } from '../../../redux/api/fymAPI';
import { chainCoverSave } from './chainCoverSave';
import { isCoverDocRefusal } from './coverEditorErrors';

/** The wait after the last change before the design is saved. */
export const COVER_AUTOSAVE_MS = 1200;

/** `invalid`: the API refused the document (`M24_COVER_DOC_INVALID`); the same save fails again. */
export type CoverSaveState = 'saved' | 'dirty' | 'saving' | 'failed' | 'invalid';

interface UseCoverAutosaveProps {
  accountId: number;
  designUuid: string;
  doc: CoverDoc;
  /** Goes up with each change of the document. */
  revision: number;
  /** False for the read-only demo artist: nothing is saved. */
  enabled: boolean;
}

/**
 * Saves the open design by itself (design cover-editor b1, "Saved"): `COVER_AUTOSAVE_MS` after the
 * last change it sends `PATCH .../cover-editor/designs/:uuid`. One save runs at a time; a change
 * during a save is saved after it. `flush` saves at once and waits (before "Use as cover", a new
 * version, a leave). A leave of the page with the component saves the last change too.
 */
export function useCoverAutosave({ accountId, designUuid, doc, revision, enabled }: UseCoverAutosaveProps) {
  const [updateDesign] = useUpdateCoverDesignMutation();
  const [saveState, setSaveState] = useState<CoverSaveState>('saved');
  const latest = useRef({ doc, revision });
  latest.current = { doc, revision };
  const savedRevision = useRef(revision);
  const running = useRef<Promise<void>>(Promise.resolve());

  // One request at a time (review fix): each save waits for the one before it, and then reads the
  // newest document again, so an older document never goes out after a newer one.
  const sendLatest = useCallback(async (): Promise<void> => {
    if (latest.current.revision === savedRevision.current) {
      return;
    }
    const sent = latest.current;
    setSaveState('saving');
    try {
      await updateDesign({ accountId, designUuid, doc: sent.doc }).unwrap();
      savedRevision.current = Math.max(savedRevision.current, sent.revision);
      setSaveState(latest.current.revision === savedRevision.current ? 'saved' : 'dirty');
    } catch (error: unknown) {
      console.error('Cover editor: the design was not saved', error);
      setSaveState(isCoverDocRefusal(error) ? 'invalid' : 'failed');
    }
  }, [accountId, designUuid, updateDesign]);

  const save = useCallback(async (): Promise<void> => {
    if (!enabled) {
      return;
    }
    const next = chainCoverSave(running.current, sendLatest);
    running.current = next;
    await next;
  }, [enabled, sendLatest]);

  useEffect(() => {
    if (!enabled || revision === savedRevision.current) {
      return undefined;
    }
    setSaveState((state) => (state === 'saving' ? state : 'dirty'));
    const timer = window.setTimeout(() => {
      save().catch(console.error);
    }, COVER_AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, revision, save]);

  // The last change goes out when the editor closes (a leave, another version).
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      saveRef.current().catch(console.error);
    },
    []
  );

  return { saveState, flush: save, hasUnsaved: saveState !== 'saved' };
}
